import React, { useEffect, useMemo, useState } from "react";
import { pdf } from "@react-pdf/renderer";
import { saveAs } from "file-saver";
import { useHistory } from "react-router-dom";
import { makeStyles } from "@material-ui/core/styles";
import {
  Box,
  Button,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Chip,
} from "@material-ui/core";
import SearchIcon from "@material-ui/icons/Search";
import InputAdornment from "@material-ui/core/InputAdornment";
import {
  Delete as DeleteIcon,
  Edit as EditIcon,
  Visibility as VisibilityIcon,
  Description as DescriptionIcon,
  PictureAsPdf as PictureAsPdfIcon,
  AttachMoney as AttachMoneyIcon,
} from "@material-ui/icons";
import Tooltip from "@material-ui/core/Tooltip";
import axios from "../../services";
import toaster from "../../utils/toaster";
import QuotationPDF from "../QuotationPDF/QuotationPDF";
import { hasQuotationPricingDetails } from "../../utils/quotationPricingStatus";

const useStyles = makeStyles((theme) => ({
  introBox: {
    display: "flex",
    flexDirection: "column",
    marginLeft: "130px",
    marginTop: "60px",
    fontFamily: "Roboto Mono, monospace",
  },
  primaryText: { fontSize: "35px", fontWeight: "bold" },
  secondaryText: { position: "relative", top: "-10px", opacity: ".78" },
  /** App shell ~100px sidebar + this page’s 130px offset; avoid maxWidth cap clipping wide tables */
  tableWrap: {
    marginLeft: "130px",
    marginTop: theme.spacing(2),
    width: "calc(100vw - 100px - 130px - 16px)",
    maxWidth: "none",
    boxSizing: "border-box",
    overflowX: "auto",
    paddingRight: theme.spacing(1),
  },
  tableScroller: {
    overflowX: "auto",
    width: "100%",
  },
  tableMinWidth: {
    minWidth: 1040,
  },
  stickyActionHeader: {
    position: "sticky",
    right: 0,
    zIndex: 4,
    backgroundColor:
      theme.palette.type === "dark" ? theme.palette.grey[800] : theme.palette.common.white,
    boxShadow: "-6px 0 12px rgba(0,0,0,0.08)",
    minWidth: 260,
    whiteSpace: "nowrap",
  },
  stickyActionCell: {
    position: "sticky",
    right: 0,
    zIndex: 2,
    backgroundColor:
      theme.palette.type === "dark" ? theme.palette.grey[900] : theme.palette.common.white,
    boxShadow: "-6px 0 12px rgba(0,0,0,0.08)",
    minWidth: 260,
    whiteSpace: "nowrap",
    verticalAlign: "top",
  },
  actionBar: {
    marginLeft: "130px",
    marginTop: theme.spacing(2),
    display: "flex",
    flexWrap: "wrap",
    alignItems: "center",
    gap: theme.spacing(2),
  },
  searchField: {
    minWidth: 280,
    maxWidth: 420,
  },
}));

const getPricingIconColor = (added) => (added ? "green" : "red");

const publicIntakeChip = (row) => {
  if (row.intakeChannel === "website_quote_form") {
    return (
      <Chip
        size="small"
        label="Filled from website"
        style={{ backgroundColor: "#e3f2fd", color: "#0d47a1", fontWeight: 600 }}
      />
    );
  }
  if (row.intakeChannel !== "public_quote_form") return null;
  if (row.customerSubmitted) {
    return (
      <Chip
        size="small"
        label="Filled by customer"
        style={{ backgroundColor: "#ffebee", color: "#c62828", fontWeight: 600 }}
      />
    );
  }
  if (row.status === "draft_customer") {
    return <Chip size="small" label="Public draft" />;
  }
  return <Chip size="small" label="Public intake" variant="outlined" />;
};

const QuotationsPage = () => {
  const classes = useStyles();
  const history = useHistory();
  const [rows, setRows] = useState([]);
  const [search, setSearch] = useState("");
  const [pdfLoadingId, setPdfLoadingId] = useState(null);

  const filteredRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((row) => {
      const name = (row.customerName || "").toLowerCase();
      const email = (row.email || "").toLowerCase();
      return name.includes(q) || email.includes(q);
    });
  }, [rows, search]);

  const loadData = async () => {
    try {
      const response = await axios.get("quotations");
      setRows(response.data || []);
    } catch (err) {
      toaster(err?.response?.data?.message || "Failed to load quotations", { type: "error" });
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const downloadQuotationPdf = async (row) => {
    setPdfLoadingId(row._id);
    try {
      const response = await axios.get(`quotations/${row._id}`);
      const blob = await pdf(<QuotationPDF data={response.data} />).toBlob();
      const safeName = (row.customerName || "quotation").replace(/[^\w-]+/g, "_");
      saveAs(blob, `Quotation-${safeName}-${String(row._id).slice(-6)}.pdf`);
      toaster("Quotation PDF downloaded");
    } catch (err) {
      console.error("Quotation PDF download:", err);
      const msg =
        err?.response?.data?.message || err?.message || "Failed to generate quotation PDF";
      toaster(msg, { type: "error" });
    } finally {
      setPdfLoadingId(null);
    }
  };

  const deleteQuotation = async (id) => {
    try {
      await axios.delete(`quotations/${id}`);
      toaster("Quotation deleted successfully");
      loadData();
    } catch (err) {
      toaster(err?.response?.data?.message || "Failed to delete quotation", { type: "error" });
    }
  };

  return (
    <>
      <Box className={classes.introBox}>
        <Typography variant="h3" component="h1" className={classes.primaryText}>
          Quotations
        </Typography>
        <Typography variant="h6" component="span" className={classes.secondaryText}>
          Manage saved quotations
        </Typography>
      </Box>
      <Box className={classes.actionBar}>
        <TextField
          className={classes.searchField}
          variant="outlined"
          size="small"
          placeholder="Search by customer name or email"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon color="action" fontSize="small" />
              </InputAdornment>
            ),
          }}
          inputProps={{ "aria-label": "Search quotations by name or email" }}
        />
        <Button variant="contained" color="primary" onClick={() => history.push("/quotations/new")}>
          New Quotation
        </Button>
      </Box>
      <Box className={classes.tableWrap}>
        <TableContainer component={Paper} className={classes.tableScroller}>
          <Table size="small" className={classes.tableMinWidth}>
            <TableHead>
              <TableRow>
                <TableCell>Customer</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Origin</TableCell>
                <TableCell>Destination</TableCell>
                <TableCell>Total Items</TableCell>
                <TableCell>Total Quantity</TableCell>
                <TableCell>Total CFT</TableCell>
                <TableCell>Source</TableCell>
                <TableCell>Created</TableCell>
                <TableCell className={classes.stickyActionHeader} align="right">
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredRows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={10}>
                    <Typography variant="body2" color="textSecondary">
                      {rows.length === 0
                        ? "No quotations yet."
                        : "No quotations match your search. Try a different name or email."}
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
              {filteredRows.map((row) => (
                <TableRow key={row._id}>
                  <TableCell>{row.customerName}</TableCell>
                  <TableCell>{row.email || "—"}</TableCell>
                  <TableCell>{row.originCity || row.originAddress1}</TableCell>
                  <TableCell>{row.destinationCity || row.destinationAddress1}</TableCell>
                  <TableCell>{row.totalItemsCount}</TableCell>
                  <TableCell>{row.totalQuantity}</TableCell>
                  <TableCell>{row.totalCft}</TableCell>
                  <TableCell>{publicIntakeChip(row) || "—"}</TableCell>
                  <TableCell>{new Date(row.createdAt).toLocaleDateString()}</TableCell>
                  <TableCell className={classes.stickyActionCell} align="right">
                    <Tooltip title="View">
                      <IconButton onClick={() => history.push(`/quotations/${row._id}`)} aria-label="View quotation">
                        <VisibilityIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Edit">
                      <IconButton onClick={() => history.push(`/quotations/${row._id}/edit`)} aria-label="Edit quotation">
                        <EditIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Open Create Job with quotation data prefilled">
                      <IconButton
                        onClick={() => history.push(`/create-job?quotationId=${row._id}`)}
                        aria-label="Create Job Docs"
                      >
                        <DescriptionIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Quotation pricing & PDF — green when pricing has been saved">
                      <IconButton
                        onClick={() => history.push(`/quotations/${row._id}/pricing`)}
                        aria-label="Quotation pricing and PDF"
                        style={{ color: getPricingIconColor(hasQuotationPricingDetails(row)) }}
                      >
                        <AttachMoneyIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Generate quotation PDF (ACE layout)">
                      <span>
                        <IconButton
                          onClick={() => downloadQuotationPdf(row)}
                          disabled={pdfLoadingId === row._id}
                          aria-label="Generate quotation PDF"
                        >
                          <PictureAsPdfIcon />
                        </IconButton>
                      </span>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <IconButton onClick={() => deleteQuotation(row._id)} aria-label="Delete quotation">
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    </>
  );
};

export default QuotationsPage;
